import {
  ACTIVE_WORKFLOW_RUN_STATUSES,
  WORKFLOW_RUN_STATUS_LABEL_KEYS,
  WORKFLOW_STEP_RUN_STATUS_LABEL_KEYS,
} from "../constants/workflow";
import type {
  IWorkflowRun,
  TWorkflowStatusLabelKey,
  TWorkflowStatusTone,
} from "../types/workflow";
import { EWorkflowRunStatus, EWorkflowStepRunStatus } from "../types/workflow";

// NOTE: NSAI-6936 — how a workflow run or step run reads on screen. Each
// platform maps the tone onto its own badge palette; the label key resolves in
// the `agents` namespace. Runs and step runs share most statuses, but each has
// its own (a run can be `cancelled`, a step `skipped` or `approved`), so a
// label is looked up in both tables.
const STATUS_TONES: Readonly<Record<string, TWorkflowStatusTone>> = {
  [EWorkflowStepRunStatus.RUNNING]: "warning",
  [EWorkflowStepRunStatus.WAITING_APPROVAL]: "info",
  [EWorkflowStepRunStatus.OK]: "success",
  [EWorkflowStepRunStatus.APPROVED]: "success",
  [EWorkflowStepRunStatus.ERROR]: "danger",
  [EWorkflowStepRunStatus.REJECTED]: "danger",
};

const WORKFLOW_STATUS_LABEL_KEYS: Readonly<
  Record<string, TWorkflowStatusLabelKey>
> = {
  ...WORKFLOW_STEP_RUN_STATUS_LABEL_KEYS,
  ...WORKFLOW_RUN_STATUS_LABEL_KEYS,
};

export const getWorkflowStatusTone = (status: string): TWorkflowStatusTone =>
  STATUS_TONES[status] ?? "neutral";

// NOTE: null for a status this client does not know, so the caller can show
// the raw value instead of a missing-key placeholder.
export const getWorkflowStatusLabelKey = (
  status: string,
): TWorkflowStatusLabelKey | null => WORKFLOW_STATUS_LABEL_KEYS[status] ?? null;

export const isWorkflowRunActive = (status: string): boolean =>
  ACTIVE_WORKFLOW_RUN_STATUSES.includes(status);

export const isWorkflowRunAwaitingApproval = (status: string): boolean =>
  status === EWorkflowRunStatus.WAITING_APPROVAL;

export const hasActiveWorkflowRun = (
  runs: readonly IWorkflowRun[] | undefined,
): boolean => (runs ?? []).some((run) => isWorkflowRunActive(run.status));
